import styled from "styled-components";
import { largeComponentHeight } from "common/const/const";
import React, { useLayoutEffect, useRef, useState } from "react";

import {
  inputBorderStyle,
  InputWrapper,
} from "common/components/Wrapper/InputBase";
import { useValidCheckSingle } from "common/hooks/validCheck";

export const StyledInputBase = styled.input`
  ${inputBorderStyle}
  align-items: center;
  width: 100%;
  height: ${largeComponentHeight};
  padding: 0;
  padding-left: ${(props) =>
    props.$hasPrefix
      ? "2rem"
      : props.iconPosition === "left"
        ? "3.5rem"
        : "1.25rem"};
  padding-right: ${(props) =>
    props.iconPosition === "right" ? "3.5rem" : "1.25rem"};

  font-family: var(--font-family);
  font-size: var(--fontSize-14);

  box-sizing: border-box;
`;

export const TextInput = ({
  //InputWrapper
  label,
  caption,
  icon,
  rules,

  //TextInput
  name,
  type = "text",
  pattern,
  placeholder,
  disabled,
  readOnly,
  defaultValue,
  onChange,
  forceError,
  prefix,
  format,
  ...rest
}) => {
  const [isComposing, setIsComposing] = useState(false);
  const inputRef = useRef(null);
  const pendingCursorRef = useRef(null);

  const { inputProps, error, onValueChange } = useValidCheckSingle({
    name: name,
    rules: rules,
    defaultValue: defaultValue,
  });

  const hasError = forceError || error;

  const rawValue = inputProps.value ?? "";

  let displayValue = rawValue;
  if (disabled) {
    displayValue = "";
  } else if (
    format &&
    rawValue !== "" &&
    rawValue !== null &&
    rawValue !== undefined &&
    !isComposing
  ) {
    displayValue = format(rawValue);
  }

  // 렌더링 커밋 직후 커서 복원
  useLayoutEffect(() => {
    if (pendingCursorRef.current !== null && inputRef.current) {
      const pos = pendingCursorRef.current;
      inputRef.current.setSelectionRange(pos, pos);
      pendingCursorRef.current = null;
    }
  });

  // ref 병합
  const setRef = (el) => {
    inputRef.current = el;
    if (typeof inputProps.ref === "function") {
      inputProps.ref(el);
    } else if (inputProps.ref) {
      inputProps.ref.current = el;
    }
  };

  return (
    <InputWrapper
      label={label}
      caption={caption}
      icon={icon}
      rules={rules}
      error={hasError}>
      <InputRow>
        {prefix && <Prefix>{prefix}</Prefix>}
        <StyledInputBase
          {...(type === "file"
            ? { ref: setRef, onBlur: inputProps.onBlur }
            : { ...inputProps, ref: setRef })}
          type={type}
          name={name}
          onCompositionStart={() => setIsComposing(true)}
          onCompositionEnd={() => setIsComposing(false)}
          onChange={(e) => {
            if (type === "file") {
              const file = e.target.files?.[0] || null;
              onChange?.(file);
              return;
            }

            const input = e.target;
            const newDisplayValue = input.value;
            const cursorPos = input.selectionStart;

            const nextRawValue = format
              ? newDisplayValue.replace(/[^\d.]/g, "")
              : newDisplayValue;

            if (isComposing || !pattern || pattern.test(nextRawValue)) {
              // 커서 앞의 유효 문자(숫자/점) 개수
              const digitsBeforeCursor = format
                ? newDisplayValue.slice(0, cursorPos).replace(/[^\d.]/g, "")
                    .length
                : cursorPos;

              // 포맷된 문자열에서 해당 개수만큼 숫자가 지난 위치 계산
              if (format && !isComposing) {
                const formatted = format(nextRawValue);
                let newPos = 0;
                let count = 0;
                for (let i = 0; i < formatted.length; i++) {
                  if (count >= digitsBeforeCursor) break;
                  if (/[\d.]/.test(formatted[i])) count++;
                  newPos = i + 1;
                }
                pendingCursorRef.current = newPos;
              }

              onValueChange(nextRawValue);
              onChange?.(nextRawValue);
            }
          }}
          placeholder={
            type === "file"
              ? undefined
              : readOnly
                ? undefined
                : disabled
                  ? inputProps.value
                  : placeholder
          }
          {...(type !== "file" && { value: displayValue })}
          disabled={disabled}
          readOnly={readOnly}
          iconPosition={icon?.position}
          $error={!!hasError}
          $hasPrefix={!!prefix}
          pattern={type === "file" ? undefined : pattern?.source}
          {...rest}
        />
        {icon?.element && icon.position === "right" && (
          <IconContainer $position="right">{icon.element}</IconContainer>
        )}
        {icon?.element && icon.position === "left" && (
          <IconContainer $position="left">{icon.element}</IconContainer>
        )}
      </InputRow>
    </InputWrapper>
  );
};

const InputRow = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  width: 100%;
`;

const Prefix = styled.span`
  position: absolute;
  left: 1.25rem;
  font-family: var(--font-family);
  font-size: var(--fontSize-14);
  pointer-events: none;
`;

const IconContainer = styled.div`
  position: absolute;
  ${(props) => props.$position}: 1rem;
  display: flex;
  align-items: center;
  height: 100%;
  top: 0;
`;
